import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InfoIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useSelector } from 'react-redux';
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { graphqlClient } from '@/utils/graphqlClient';
import { getAllTagsQuery } from '@/graphql/queries/getAllTags';

// Define the GraphQL response type
interface TagsQueryResponse {
  tags: Tag[];
}
import { addPageTagMutation } from '@/graphql/mutations/addPageTag';
import DeletePageTagModal from '@/components/company/DeletePageTagModal';

interface PageTagsProps {
  pageSubslug?: string | null;
}

// Define the tag structure to match what's in the GraphQL schema
interface Tag {
  id: string;
  name: string;
  description: string | null;
}

// Quarter information structure
interface Quarter {
  quarter: string;
  version: string;
  ids: string[];
  screenshot_url: string;
  verified: boolean;
}

// Function to get current quarter (like "Q2 2025")
const getCurrentQuarter = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  return `Q${quarter} ${year}`;
};

const PageTags: React.FC<PageTagsProps> = ({ pageSubslug }) => {
  const { toast } = useToast();
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [currentQuarter, setCurrentQuarter] = useState<string>(getCurrentQuarter());
  const [displayingQuarter, setDisplayingQuarter] = useState<string>(getCurrentQuarter());
  const [usingOlderData, setUsingOlderData] = useState<boolean>(false);

  // Get the GraphQL data from Redux store
  const graphQLData = useSelector((state: any) => state.graphql.data);
  const graphQLLoading = useSelector((state: any) => state.graphql.loading);

  // Holds the current page data
  const [currentPage, setCurrentPage] = useState<any>(null);
  // Holds the tags to display
  const [pageTags, setPageTags] = useState<Tag[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newTagId, setNewTagId] = useState<string>('');
  const [deleteTagObj, setDeleteTagObj] = useState<Tag | null>(null);

  // Fetch all available tags using the GraphQL query
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setAllTags([]); // Clear any existing tags
        console.log('🏷️ Fetching all available tags via GraphQL');
        
        const response = await graphqlClient.request<TagsQueryResponse>(getAllTagsQuery);
        if (response && response.tags) {
          console.log(`✅ Successfully fetched ${response.tags.length} tags`);
          setAllTags(response.tags);
        }
      } catch (error) {
        console.error('❌ Error fetching tags:', error);
        toast({ title: 'Error', description: 'Failed to load tags', variant: 'destructive' });
      }
    };
    
    fetchTags();
  }, [toast]);

  useEffect(() => {
    if (!graphQLData || graphQLLoading) return;

    // Find the current page data based on subslug
    const page = pageSubslug 
      ? graphQLData.pages?.find((p: any) => p.subslug === pageSubslug)
      : graphQLData.pages?.[0]; // Default to first page if no subslug

    setCurrentPage(page);

    if (page?.tags) {
      // Set the page tags
      setPageTags(page.tags);
    } else {
      setPageTags([]);
    }

    // Determine quarters and find the one to display
    if (page?.quarters && page.quarters.length > 0) {
      // Sort quarters in descending order (most recent first)
      const sortedQuarters = [...page.quarters].sort((a, b) => {
        // Assuming quarter is in format "Q1 2025"
        const [aQ, aYear] = a.quarter.split(' ');
        const [bQ, bYear] = b.quarter.split(' ');
        
        if (aYear !== bYear) return parseInt(bYear) - parseInt(aYear);
        return parseInt(bQ.substring(1)) - parseInt(aQ.substring(1));
      });

      // Find the current quarter in the data
      const foundCurrentQuarter = sortedQuarters.find(q => q.quarter === currentQuarter);

      if (foundCurrentQuarter && page.tags && page.tags.length > 0) {
        setDisplayingQuarter(currentQuarter);
        setUsingOlderData(false);
      } else {
        // Find most recent quarter that has tags
        const recentQuarterWithTags = sortedQuarters[0];
        if (recentQuarterWithTags) {
          setDisplayingQuarter(recentQuarterWithTags.quarter);
          setUsingOlderData(recentQuarterWithTags.quarter !== currentQuarter);
        }
      }
    }
  }, [graphQLData, graphQLLoading, pageSubslug, currentQuarter]);

  const handleAddTag = async () => {
    if (!newTagId || !currentPage) return;
    try {
      const version = currentPage.quarters?.[0]?.version || '';
      const response = await addPageTagMutation({
        slug: graphQLData.slug,
        page_id: currentPage.id,
        tag_id: newTagId,
        version
      });
      if (response.addPageTag) {
        setPageTags([...pageTags, {
          id: response.addPageTag.id,
          name: response.addPageTag.name || '',
          description: response.addPageTag.description || null
        }]);
        toast({ title: 'Tag added' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to add tag', variant: 'destructive' });
    } finally {
      setAddDialogOpen(false);
      setNewTagId('');
    }
  };

  if (graphQLLoading) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Page Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading tags...</div>
        </CardContent>
      </Card>
    );
  }

  if (!currentPage) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Page Tags</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No page data found.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card className="mt-4">
      <CardHeader className="pb-1 pt-3">
        <CardTitle className="flex items-center justify-between">
          <span>Page Tags</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setAddDialogOpen(true)}>Add Tag</Button>
            {usingOlderData && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center">
                    <Badge variant="outline" className="ml-2 text-amber-500 border-amber-500">
                      {displayingQuarter}
                    </Badge>
                    <InfoIcon className="h-4 w-4 ml-1 text-amber-500" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Showing tags from previous quarter ({displayingQuarter}).</p>
                  <p>No tags available for current quarter.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pageTags.length === 0 ? (
          <div className="text-sm text-muted-foreground">No tags found for this page.</div>
        ) : (
          <div>
            <ScrollArea className="h-[120px] pr-4">
              <div className="flex flex-wrap gap-2">
                {pageTags.map((tag) => (
                  <div key={tag.id} className="inline-block">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Badge
                          variant="secondary"
                          className="cursor-pointer hover:bg-secondary/80"
                          onClick={() => setSelectedTag(tag)}
                        >
                          {tag.name}
                        </Badge>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>{tag.name}</DialogTitle>
                          <DialogDescription>
                            {tag.description}
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="sm:justify-between flex-row-reverse">
                          <Button variant="destructive" size="sm" onClick={() => setDeleteTagObj(tag)}>
                            Remove Tag
                          </Button>
                          <DialogClose asChild>
                            <Button type="button" variant="secondary">Close</Button>
                          </DialogClose>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>

    <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Tag</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Select value={newTagId} onValueChange={setNewTagId}>
            <SelectTrigger>
              <SelectValue placeholder="Select tag" />
            </SelectTrigger>
            <SelectContent>
              {allTags.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="sm:justify-end">
          <Button onClick={handleAddTag} disabled={!newTagId}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {deleteTagObj && (
      <DeletePageTagModal
        isOpen={!!deleteTagObj}
        onOpenChange={(open) => { if (!open) setDeleteTagObj(null); }}
        slug={graphQLData.slug}
        pageId={currentPage.id}
        tagId={deleteTagObj.id}
        tagName={deleteTagObj.name}
        onSuccess={() => {
          setPageTags(pageTags.filter(t => t.id !== deleteTagObj.id));
          setDeleteTagObj(null);
        }}
      />
    )}
    </>
  );
};

export default PageTags;
